import type { ReactNode, RefObject } from 'react'
import { Braces, Copy, Download, Ellipsis, Eye, EyeOff, LineChart, Search, Settings } from 'lucide-react'
import type { Chart } from 'klinecharts'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/useIsMobile'
// Relative imports (not the '@/components' barrel) to avoid a cycle now that
// the barrel exports Workbench, which renders this file.
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../Tooltip/Tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../DropdownMenu/DropdownMenu'
import { WorkbenchTimeframe } from './WorkbenchTimeframe'
import { WorkbenchChartType } from './WorkbenchChartType'
import { WorkbenchCamera } from './WorkbenchCamera'
import { useWorkbenchSnapshot } from './useWorkbenchSnapshot'
import { CHART_TYPES, TIMEFRAME_LABELS, TIMEFRAME_OPTIONS } from './catalog'
import type { ChartTypeId } from './types'

// Top toolbar for the Strategy Design chart. Desktop lays out the chart
// controls in TradingView order — symbol search · timeframe · chart-type ·
// indicators on the left, the walkthrough switch and utility actions (camera,
// settings, show-state) on the right. Mobile keeps only the symbol and the
// walkthrough switch visible and folds everything else into an overflow menu.

export function ToolbarDivider() {
  return <div className="w-px h-5 bg-border mx-0.5 shrink-0" />
}

export interface WorkbenchTopToolbarProps {
  chart: Chart | null
  captureRef: RefObject<HTMLDivElement | null>
  symbol: string
  onOpenSymbolSearch: () => void
  timeframe: string
  onTimeframeChange: (tf: string) => void
  chartType: ChartTypeId
  onChartTypeChange: (id: ChartTypeId) => void
  onOpenIndicators: () => void
  onOpenSettings: () => void
  onShowState: () => void
  /** The walkthrough is on the chart — its trades, its tinted sections and the band under it. */
  walkthroughShown?: boolean
  /**
   * The Walkthrough toggle was pressed. With a run on this symbol that shows or hides it; with
   * none, turning it on runs one over the period the user drew on.
   */
  onWalkthroughShownChange?: (next: boolean) => void
  /** A run exists for this symbol, so the toggle shows or hides rather than runs one. */
  walkthroughExists?: boolean
  /**
   * Why the toggle is unavailable. Disables it and explains it on hover.
   *
   * Deliberately a message rather than a boolean: "you can't do this" without "here's what
   * to do first" is the worst version of a disabled control, and every blocked case here has
   * a concrete next step (draw something, generate the code).
   */
  walkthroughDisabledReason?: string | null
  /**
   * The previous / next trade idea arrows. A slot, rendered by Workbench: they follow the chart's
   * every scroll, and re-rendering the toolbar with them would re-render all of it.
   */
  ideaNav?: ReactNode
}

export function WorkbenchTopToolbar(props: WorkbenchTopToolbarProps) {
  const isMobile = useIsMobile()
  return isMobile ? <MobileToolbar {...props} /> : <DesktopToolbar {...props} />
}

function ToolbarShell({ children }: { children: ReactNode }) {
  return <div className="shrink-0 h-10 flex items-center gap-1 px-2 border-b border-border bg-background">{children}</div>
}

function SymbolButton({
  symbol,
  onOpenSymbolSearch,
}: Pick<WorkbenchTopToolbarProps, 'symbol' | 'onOpenSymbolSearch'>) {
  return (
    <button
      id="symbol-search-btn"
      type="button"
      onClick={onOpenSymbolSearch}
      className="inline-flex items-center gap-1.5 h-7 pl-2 pr-2.5 rounded-md hover:bg-muted/60 transition-colors cursor-pointer shrink-0"
    >
      <Search size={14} strokeWidth={1.75} className="text-muted-foreground" />
      <span className="text-label font-semibold text-foreground tracking-tight">{symbol}</span>
    </button>
  )
}

/** A hover explanation for a toolbar control, disabled or not. */
function Hint({ content, children }: { content: ReactNode; children: ReactNode }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        {/* A disabled control fires no pointer events, so the trigger wraps it. */}
        <TooltipTrigger asChild>
          <span className="inline-flex shrink-0">{children}</span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[15rem]">
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/**
 * The walkthrough's one switch: on, the run is on the chart.
 *
 * The eye and its label are the whole control. Open and in full colour, the run is on the chart;
 * crossed out and muted, it is hidden. With no run for this symbol yet, turning it on runs one
 * over the drawings as they are; the band under the chart says "Preparing…" meanwhile, and the
 * switch stays on until it lands.
 */
function WalkthroughToggle({
  walkthroughShown = false,
  onWalkthroughShownChange,
  walkthroughExists = false,
  walkthroughDisabledReason = null,
}: Pick<
  WorkbenchTopToolbarProps,
  'walkthroughShown' | 'onWalkthroughShownChange' | 'walkthroughExists' | 'walkthroughDisabledReason'
>) {
  const blocked = !!walkthroughDisabledReason
  const hint =
    walkthroughDisabledReason ??
    (!walkthroughExists
      ? 'Run a walkthrough of your trades'
      : walkthroughShown
        ? 'Hide the walkthrough'
        : 'Show the walkthrough on the chart')
  const Glyph = walkthroughShown ? Eye : EyeOff
  return (
    <Hint content={hint}>
      <button
        type="button"
        role="switch"
        aria-checked={walkthroughShown}
        disabled={blocked}
        onClick={() => onWalkthroughShownChange?.(!walkthroughShown)}
        className={cn(
          'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-label transition-colors shrink-0 select-none',
          blocked
            ? 'text-text-disabled cursor-not-allowed'
            : walkthroughShown
              ? 'text-foreground hover:bg-muted/60 cursor-pointer'
              : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground cursor-pointer',
        )}
      >
        <Glyph size={14} strokeWidth={1.75} aria-hidden data-glyph={walkthroughShown ? 'eye' : 'eye-off'} className="shrink-0" />
        {/* Below 400px the phone row also carries the idea arrows: the word gives way, and
            stays the switch's name for assistive tech. */}
        <span className="max-[399px]:sr-only">Walkthrough</span>
      </button>
    </Hint>
  )
}

function DesktopToolbar(props: WorkbenchTopToolbarProps) {
  const { chart, captureRef, timeframe, onTimeframeChange, chartType, onChartTypeChange, onOpenIndicators, onOpenSettings, onShowState } = props
  return (
    <ToolbarShell>
      {/* left group: market + chart-shape controls */}
      <SymbolButton {...props} />
      <ToolbarDivider />
      <WorkbenchTimeframe value={timeframe} onChange={onTimeframeChange} />
      <ToolbarDivider />
      <WorkbenchChartType value={chartType} onChange={onChartTypeChange} />
      <ToolbarDivider />
      <button
        id="indicators-btn"
        type="button"
        onClick={onOpenIndicators}
        className={cn(
          'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-label transition-colors cursor-pointer',
          'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        )}
      >
        <LineChart size={14} strokeWidth={1.75} />
        Indicators
      </button>
      {props.ideaNav}

      {/* right group: utility actions */}
      <div className="ml-auto flex items-center gap-1">
        <WalkthroughToggle {...props} />
        <ToolbarDivider />
        <WorkbenchCamera chart={chart} captureRef={captureRef} />
        <button
          type="button"
          onClick={onOpenSettings}
          aria-label="Chart settings"
          className={cn(
            'inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors cursor-pointer',
            'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
          )}
        >
          <Settings size={15} strokeWidth={1.75} />
        </button>
        {/* Dev-only aid: ships hidden. Re-enable from devtools with
            `display: inline-flex` on [data-debug="show-state"]. */}
        <button
          type="button"
          onClick={onShowState}
          aria-label="Show State"
          data-debug="show-state"
          className={cn(
            'hidden items-center gap-1.5 h-7 px-2.5 rounded-md text-label transition-colors cursor-pointer',
            'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
          )}
        >
          <Braces size={14} strokeWidth={1.75} />
          Show State
        </button>
      </div>
    </ToolbarShell>
  )
}

function MobileToolbar(props: WorkbenchTopToolbarProps) {
  const { chart, captureRef, timeframe, onTimeframeChange, chartType, onChartTypeChange, onOpenIndicators, onOpenSettings, onShowState } = props
  const snapshot = useWorkbenchSnapshot(chart, captureRef)
  const tfLabel = (TIMEFRAME_LABELS as Record<string, string>)[timeframe] ?? timeframe
  return (
    <ToolbarShell>
      <SymbolButton {...props} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="More tools"
            className={cn(
              'inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors cursor-pointer shrink-0',
              'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
            )}
          >
            <Ellipsis size={16} strokeWidth={1.75} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <span className="flex-1">Timeframe</span>
              <span className="text-muted-foreground tabular-nums mr-1">{tfLabel}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={timeframe} onValueChange={onTimeframeChange}>
                {TIMEFRAME_OPTIONS.map((tf) => (
                  <DropdownMenuRadioItem key={tf} value={tf} className="tabular-nums">
                    {TIMEFRAME_LABELS[tf]}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Chart type</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={chartType} onValueChange={(v) => onChartTypeChange(v as ChartTypeId)}>
                {CHART_TYPES.map((t) => (
                  <DropdownMenuRadioItem key={t.id} value={t.id}>
                    {t.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => onOpenIndicators()}>
            <LineChart size={14} strokeWidth={1.75} />
            Indicators…
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void snapshot.save()}>
            <Download size={14} strokeWidth={1.75} />
            Save chart image
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void snapshot.copy()}>
            <Copy size={14} strokeWidth={1.75} />
            Copy chart image
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onOpenSettings()}>
            <Settings size={14} strokeWidth={1.75} />
            Chart settings…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {props.ideaNav}
      <div className="ml-auto flex items-center gap-1">
        {/* The switch keeps its word even here — a bare tick would not say what it shows. */}
        <WalkthroughToggle {...props} />
        {/* Dev-only aid, ships hidden (same devtools re-enable as desktop). */}
        <button
          type="button"
          onClick={onShowState}
          aria-label="Show State"
          data-debug="show-state"
          className="hidden items-center gap-1.5 h-7 px-2 rounded-md text-label text-muted-foreground cursor-pointer"
        >
          <Braces size={14} strokeWidth={1.75} />
        </button>
      </div>
    </ToolbarShell>
  )
}
